import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getProxiedCloudinaryUrl } from '../utils/cloudinaryProxy';
import './Login.css';

// Simple toast implementation
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 4000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`toast toast-${type}`}>
      {message}
      <button onClick={onClose} className="toast-close">&times;</button>
    </div>
  );
};

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [shake, setShake] = useState(false);
  const [toast, setToast] = useState(null);
  const { signIn } = useAuth();
  const navigate = useNavigate();

  const showToast = (message, type = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  useEffect(() => {
    // Check if user is already authenticated and redirect to POS if so
    const token = localStorage.getItem('auth_token');
    const username = localStorage.getItem('username');
    if (token && username) {
      console.log('User already authenticated, redirecting to POS');
      navigate('/pos', { replace: true });
      return;
    }

    // Set background image using proxy URL
    const backgroundUrl = getProxiedCloudinaryUrl("https://res.cloudinary.com/foodbaoimg/image/upload/v1755740468/red_theme_tjyai9.jpg");
    const loginPage = document.querySelector('.login-page');
    if (loginPage) {
      loginPage.style.setProperty('--background-image', `url(${backgroundUrl})`);
    }

    // Check URL parameters for messages
    const params = new URLSearchParams(window.location.search);
    const reason = params.get('reason');
    const expired = params.get('expired');

    if (reason === 'expired' || expired === 'true') {
      showToast('Your session has expired. Please login again.', 'warning');
    } else if (reason === 'security') {
      showToast('You have been logged out for security reasons.', 'warning');
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const { error } = await signIn(username, password);

      if (error) {
        setError(error.message);
        showToast(error.message, 'error');
        setShake(true);
        setTimeout(() => setShake(false), 500);
        return;
      }

      // Show success message
      showToast('Login successful! Redirecting...', 'success');

      // Wait a bit for the authentication state to be properly set
      setTimeout(() => {
        // Double-check that user is authenticated before navigating
        if (localStorage.getItem('auth_token') && localStorage.getItem('username')) {
          navigate('/pos');
        } else {
          // If tokens are not set, show error
          setError('Authentication failed. Please try again.');
          showToast('Authentication failed. Please try again.', 'error');
        }
      }, 500);

    } catch (err) {
      const errorMessage = 'Network error or unexpected issue. Please try again.';
      setError(errorMessage);
      showToast(errorMessage, 'error');
      setShake(true);
      setTimeout(() => setShake(false), 500);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      {/* Toast Notifications */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* Loading Overlay */}
      {loading && (
        <div className="loading-overlay visible">
          <div className="loading-spinner"></div>
        </div>
      )}

      {/* Session Message Banner */}
      {error && (
        <div className="session-message">
          {error}
        </div>
      )}

      <div className="container">
        <div className="row">
          <div className={`col s12 m10 l8 offset-m1 offset-l2 login-container ${shake ? 'shake-animation' : ''}`}>
            <form id="login-form" onSubmit={handleSubmit}>
              {/* Logo */}
              <div className="logo-container">
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <img
                    src={getProxiedCloudinaryUrl("https://res.cloudinary.com/foodbaoimg/image/upload/v1755740468/fblogo_zj8a5s.png")}
                    alt="FoodBao Logo"
                    style={{
                      width: '100px',
                      height: '60px',
                      borderRadius: '0',
                      objectFit: 'fill',
                      background: '#fff',
                      padding: '0',
                      marginBottom: '18px'
                    }}
                  />
                </div>
              </div>

              {/* Username Input */}
              <div className="animated-input input-field">
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  autoComplete="username"
                />
                <label htmlFor="username">Username</label>
                <i className="material-icons input-icon">person</i>
              </div>

              {/* Password Input */}
              <div className="animated-input input-field">
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                />
                <label htmlFor="password">Password</label>
                <i className="material-icons input-icon">lock</i>
              </div>

              {/* Forgot Password Link */}
              <div className="forgot-password-container">
                <a href="/forgot-password" className="forgot-password-link">
                  Forgot Password?
                </a>
              </div>

              {/* Login Button */}
              <div className="btn-login-container">
                <button type="submit" className="btn-login waves-effect waves-light" disabled={loading}>
                  <i className="material-icons">login</i>
                  <span>{loading ? 'Signing In...' : 'Sign In'}</span>
                </button>
              </div>

              {/* Footer */}
              <div className="login-footer">
                <p>Don't have an account? <a href="/register">Register here</a></p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;